import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Hotjar from '@hotjar/browser';
import { ToastService } from 'asksuite-citrus';
import { merge, of, ReplaySubject, throwError } from 'rxjs';
import { finalize, mergeMap, take, tap } from 'rxjs/operators';
import { FeatureToggleService } from './feature-toggle.service';
import { FeatureToggles } from '../enums/feature-toggles.enum';
import { LiveChatStateService } from 'src/app/modules/live-chat/services/live-chat-state.service';
import { environment } from 'src/environments/environment';
import { generateRequestTimeLogHeader } from '../interceptors/log.interceptor';
import { Company } from '../interfaces/company';
import { EnabledEconomicQuotes, Start, StartCompany } from '../interfaces/start';
import { User } from '../interfaces/user';
import { CompanyService } from './company.service';
import { UserService } from './user.service';
import { LocalizationService } from './localization.service';
import { LoggerService } from './logger.service';
import { LogEvent } from '../enums/log-event';
import { getLocalstorageSizes } from 'src/app/modules/live-chat/helpers/localstorage-sizes';
import { SessionService } from './session.service';
import { CompanyBlockDataService } from './company-block-data.service';
import { StateActionEventBufferService } from './state-action-event-buffer.service';

@Injectable({
  providedIn: 'root',
})
export class StartService {
  // sessionReady$ keeps its narrow, honest meaning: session + companies
  // genuinely loaded. sessionFailed$ is the counterpart for the error path.
  // Consumers that only care "is the bootstrap attempt over, one way or
  // another" (so they don't hang forever) should use sessionSettled$ —
  // that's every current gated consumer (SessionGateInterceptor,
  // ZoidAppService, FilterService, ChatInputVisibilityService).
  private readonly sessionReadySub = new ReplaySubject<void>(1);
  public readonly sessionReady$ = this.sessionReadySub.asObservable();

  private readonly sessionFailedSub = new ReplaySubject<void>(1);
  public readonly sessionFailed$ = this.sessionFailedSub.asObservable();

  public readonly sessionSettled$ = merge(
    this.sessionReady$,
    this.sessionFailed$
  ).pipe(take(1));

  constructor(
    private httpClient: HttpClient,
    private sessionService: SessionService,
    private livechatStateService: LiveChatStateService,
    private companyService: CompanyService,
    private toastService: ToastService,
    private localizeService: LocalizationService,
    private companyBlockDataService: CompanyBlockDataService,
    private featureToggleService: FeatureToggleService,
    private stateActionEventBufferService: StateActionEventBufferService,
    private logger: LoggerService,
    private userService: UserService
  ) {}

  public initialize() {
    this.companyService.setLoading(true);
    setTimeout(() => {
      //FIXME: gambiarra para parar o loading
      this.companyService.setLoading(false);
    }, 5000);

    this.prepareCompaniesAndUsers()
      .subscribe({
        next: () => {
          this.sessionReadySub.next();
        },
        error: (error) => {
          this.logger.error(LogEvent.LIVECHAT_START_SESSION_ERROR, error);

          this.toastService.danger(
            this.localizeService.key('ERROR_WHILE_REFRESHING_COMPANIES')
          );

          this.sessionFailedSub.next();
        },
      });
  }

  public prepareCompaniesAndUsers() {
    return this.getCompaniesFromUser().pipe(
      tap(({ user }) => {
        if (user) {
          this.updateSessionFromUser(user);
          this.configureHotjar();
        }
      }),
      tap(() => this.companyService.setLoading(true)),
      mergeMap(({ companies }: Start) => {
        if (typeof companies === 'string') {
          return this.getCompaniesFromUrl(companies);
        } else if (Array.isArray(companies)) {
          return of(companies);
        } else {
          return throwError(() => new Error('Invalid companies type'));
        }
      }),
      tap((companies) => this.handleUpdateSession(companies)),
      finalize(() => this.companyService.setLoading(false))
    );
  }

  public getCompaniesFromUser() {
    return this.httpClient.get<Start>(
      `${environment.externalUrls.bff}/start/livechat`,
      { headers: generateRequestTimeLogHeader('start-livechat') }
    );
  }

  private updateSessionFromUser(user: User) {
    const session = this.sessionService.get();
    const isGlobal = user.role === 'global';

    this.sessionService.set({
      ...(session || {}),
      user,
      // Drop any stale persisted companies list right away for global users
      // — handleUpdateSession() won't repersist it, but this shallow-merge
      // would otherwise carry an old (possibly huge) list forward as-is.
      ...(isGlobal && { companies: undefined }),
    });
  }

  private getCompaniesFromUrl(url: string) {
    return this.httpClient.get<StartCompany[]>(url, {
      headers: generateRequestTimeLogHeader('initial-companies-list'),
    });
  }

  private handleUpdateSession(companies: StartCompany[]) {
    this.companyBlockDataService.initializeFromCompanies(companies);

    const userHasOnlyOneCompany =
      companies.length > 0 && companies.length === 1;
    this.livechatStateService.setUserHasOnlyOneCompany(userHasOnlyOneCompany);

    const userHasWhatsappPermission = this.verifyCompanyHasWhatsapp(companies);
    this.livechatStateService.setUserHasWhatsappPermission(
      userHasWhatsappPermission
    );

    const userHasWhatsappNumberPermission = this.verifyCompanyHasWhatsappNumber(companies);
    this.livechatStateService.setUserHasWhatsappNumberPermission(
      userHasWhatsappNumberPermission
    );

    const languagesCompany = this.verifyLanguagesCompany(companies);
    this.livechatStateService.setLanguagesCompany(languagesCompany);

    const userHasPhonePermission = this.verifyCompanyHasPhone(companies);
    this.livechatStateService.setUserHasPhonePermission(userHasPhonePermission);

    const updatedCompanies = this.updatedCompanies(companies);

    if (updatedCompanies.length > 0) {
      this.companyService.setCompanies(updatedCompanies);

      // Global users can have thousands of companies — persisting the full
      // list would blow past localStorage's quota (QuotaExceededError on
      // Safari/iOS). They keep companies in memory only, refetched on load.
      if (!this.userService.isCurrentUserGlobal()) {
        const session = this.sessionService.get();
        this.sessionService.set({
          ...(session || {}),
          companies: updatedCompanies,
        });
      }
    }

    this.checkBlockContactFeatureToggle();
    this.livechatStateService.setKanbanWaitingTimeEnabled(true);
  }

  private checkBlockContactFeatureToggle(): void {
    of(true)
      .subscribe((enabled) => {
        this.livechatStateService.setBlockContactFeatureEnabled(enabled);
      });
  }

  private updatedCompanies(companies: StartCompany[]): Company[] {
    return companies.map((company) => ({
      companyId: company.companyId,
      companyName: company.json.hotelName,
      hasWhatsapp: this.companyHasWhatsapp(company),
      whatsappNumber: company.json.whatsAppNumberGupshup,
      hasWhatsappNumber: this.companyHasWhatsappNumber(company),
      hasWhatsappCredits: this.companyHasWhatsappCredits(company),
      languagesCompany: this.getLanguagesCompany(company),
      hasEmail: this.companyHasEmail(company),
      companyEmail: company.json.nylasEmail,
      companyEmailToken: company.json.nylasTokenId,
      companyEmailAccountId: company.json.nylasAccountId,
      companyEmailGrantId: company.json.nylasGrantId,
      hasPhone: this.companyHasPhone(company),
      companyPhoneNumber: company.json.code7PhoneNumber,
      newUraIdFlowIvr: company.json.newUraIdFlowIvr,
      newUraCampaignId: company.json.newUraCampaignId,
      newUraOrganizationId: company.json.newUraOrganizationId,
      code7OrganizationId: company.json.code7OrganizationId,
      blockedSenders: company.json.blockedSenders,
      blockedSendersWhatsapp: company.json.blockedSendersWhatsapp,
      blockedSendersPhone: company.json.clientPhones,
      bookingEngineValidations: company.bookingEngineValidations,
      defaultLanguage: company.defaultLanguage,
      defaultCurrency: company.json.defaultCurrency,
      disableWhatsappEconomicTextOrImageMode: company.json.disableWhatsappEconomicTextOrImageMode,
      forceWhatsappEconomyMode: company.json.forceWhatsappEconomyMode,
      enabledEconomicQuotes: this.resolveEnabledEconomicQuotes(company.json.enabledEconomicQuotes),
    }));
  }

  // TAS-584: campo ausente ou parcial no company.json = todos os estilos
  // ativos, mesma regra do asksuite-dashboard (resolveEnabledEconomicQuotes
  // em company.service.ts la).
  private resolveEnabledEconomicQuotes(
    stored?: Partial<EnabledEconomicQuotes>
  ): EnabledEconomicQuotes {
    return {
      traditional: stored?.traditional ?? true,
      pdf: stored?.pdf ?? true,
      image: stored?.image ?? true,
    };
  }

  private verifyCompanyHasWhatsapp(companies: StartCompany[]) {
    return companies.some((company) => this.companyHasWhatsapp(company));
  }

  private verifyCompanyHasWhatsappNumber(companies: StartCompany[]) {
    return companies.some((company) => this.companyHasWhatsappNumber(company));
  }

  private verifyLanguagesCompany(companies: StartCompany[]) {
    return companies.some((company) => this.getLanguagesCompany(company));
  }

  private companyHasWhatsapp(company: StartCompany) {
    return (
      (company.json.whatsAppNameGupshup && company.gupshup) || company.zaas
    );
  }

  // Critério único das telas de créditos de WhatsApp, espelhado do asksuite-dashboard
  // (`companyHasWhatsappCredits` em company.service.ts): Gupshup ativo com número.
  public companyHasWhatsappCredits(company: StartCompany): boolean {
    return !!company.json.whatsAppNumberGupshup && !!company.gupshup;
  }

  private companyHasEmail(company: StartCompany): boolean {
    return !!(
      company.json.nylasEmail &&
      (company.json.nylasGrantId || company.json.nylasTokenId)
    );
  }

  private companyHasPhone(company: StartCompany): boolean {
    return !!company.json.enableCode7VoiceCall;
  }

  private verifyCompanyHasPhone(companies: StartCompany[]) {
    return companies.some((company) => this.companyHasPhone(company));
  }

  //TODO: verficiar se tirar arrow function da problema
  private configureHotjar() {
    if (
      environment.app.production &&
      environment.hotjar.siteId &&
      environment.hotjar.version
    ) {
      Hotjar.init(environment.hotjar.siteId, environment.hotjar.version);
      const session = this.sessionService.get();

      if (session?.user && Hotjar.isReady()) {
        Hotjar.identify(session.user.id.toString(), {
          userId: session.user.id,
          email: session.user.email,
          role: session.user.role,
          userRole: session.user.userRole.toString(),
          companiesIds: session.user.companiesIds.toString(),
          countCompaniesIds: session.user.companiesIds.length,
          defaultLanguage: session.user.defaultLanguage,
          selectedLanguage: this.localizeService.selectedLanguage || '',
        });
      }
    }
  }

  public companyHasWhatsappNumber(company: StartCompany): boolean {
    const { json, gupshup, zaas } = company;
    const hasGupshupWhatsapp = gupshup && !!json.whatsAppNameGupshup && !!json.whatsAppNumberGupshup;
    const hasZaasWhatsapp    = zaas && (!!json.whatsAppNumberZass || !!json.whatsAppNumber);
    return hasGupshupWhatsapp || hasZaasWhatsapp;
  }

  public getLanguagesCompany(company: StartCompany){
    return company.languages;
  }
}
